/** 配對窗戶：回播／對方列表的站方時間與本地時鐘差 10 分鐘內都算同一句。 */
export const PEER_MATCH_WINDOW_MS = 10 * 60_000;
/** 一次最多問幾個身分（每次是一個 `GET /events`；不要為了「更確定」打一排請求）。 */
export const MAX_PEER_CANDIDATES = 3;
/** 一次驗證最多抓幾筆事件（只要最近那幾句，抓多只是浪費頻寬）。 */
export const PEER_EVENT_LIMIT = 50;
/**
 * 送達後多久才去問第二個連線（毫秒）。
 * UT 的 `r.phtml` 是輪詢、對方列表要下一次輪詢才含這句，立刻問一定撲空；
 * 1.5 秒＝一個輪詢週期的保守值（實測見 `notes/delivery-peer-probe.log`）。
 */
export const PEER_VERIFY_DELAY_MS = 1_500;
function norm(value) {
    return (value ?? '').trim();
}
/**
 * 從清單挑「能替這則訊息作證」的身分。
 *
 * - 公開訊息：同 service、同 room、不是自己（UT 房號由站方改派，必須用房號比對，不能比 MID）。
 * - 私訊：只有收件人本人看得到 → 暱稱要對上 `query.to`，房間不納入條件
 *   （收件人可能已經換房，但訊息還在它的列表裡）。
 */
export function pickPeerCandidates(sessions, self, query) {
    const wanted = norm(query.to);
    const selfRoom = norm(self.room);
    const picked = sessions.filter((session) => {
        if (session.id === self.id)
            return false;
        if (session.service !== self.service)
            return false;
        if (query.kind === 'pm')
            return wanted !== '' && norm(session.nickname) === wanted;
        if (!selfRoom)
            return false;
        return norm(session.room) === selfRoom;
    });
    return picked.slice(0, MAX_PEER_CANDIDATES);
}
/**
 * 在「另一個身分的事件列表」裡找這一句。
 * 回 `null` = 沒看到（＝這條連線拿不到證據，不代表沒送出）。
 */
export function findPeerEcho(events, query) {
    const text = norm(query.text);
    if (text === '')
        return null;
    const wantedTo = norm(query.to);
    let fallback = null;
    for (let index = 0; index < events.length; index += 1) {
        const event = events[index];
        if (!event)
            continue;
        if (event.type !== query.kind)
            continue;
        if (norm(event.text) !== text)
            continue;
        if (query.kind === 'pm' && norm(event.to) !== wantedTo)
            continue;
        // 窗戶對著**本地送出時間**比（不是「現在」）：站方時鐘差幾秒，`ts` 永遠不會剛好相等。
        if (Math.abs((event.ts || query.ts) - query.ts) > PEER_MATCH_WINDOW_MS)
            continue;
        if (norm(event.from) === norm(query.from))
            return { index, fromMatched: true };
        fallback ??= { index, fromMatched: false };
    }
    return fallback;
}
const defaultSleep = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});
/**
 * 依序問每一個候選身分「你那裡看得到這句話嗎」。
 * 一看到就停（後面的不用再問）；全部失敗才算 `error`，單一請求失敗不影響其他身分。
 */
export async function verifyByPeer(client, candidates, query, options = {}) {
    const now = options.now ?? Date.now;
    const startedAt = now();
    const checks = [];
    if (candidates.length === 0) {
        return { status: 'no-peer', checks, latencyMs: 0, at: startedAt };
    }
    if (!client.listEvents) {
        return {
            status: 'error',
            checks: candidates.map((c) => ({ id: c.id, nickname: c.nickname, found: false, error: '沒有 listEvents' })),
            latencyMs: 0,
            at: startedAt,
        };
    }
    const sleep = options.sleep ?? defaultSleep;
    const gap = options.gapMs ?? 400;
    for (const candidate of candidates) {
        try {
            const page = await client.listEvents(candidate.id, { limit: options.limit ?? PEER_EVENT_LIMIT });
            const hit = findPeerEcho(page.events, query);
            checks.push({
                id: candidate.id,
                nickname: candidate.nickname,
                found: hit !== null,
                ...(hit ? { fromMatched: hit.fromMatched } : {}),
            });
            if (hit) {
                return { status: 'verified', checks, latencyMs: now() - startedAt, at: now() };
            }
        }
        catch (err) {
            checks.push({
                id: candidate.id,
                nickname: candidate.nickname,
                found: false,
                error: err instanceof Error ? err.message : String(err),
            });
        }
        if (gap > 0)
            await sleep(gap);
    }
    const allFailed = checks.every((check) => check.error !== undefined);
    return {
        status: allFailed ? 'error' : 'not-found',
        checks,
        latencyMs: now() - startedAt,
        at: now(),
    };
}
/** 結果翻成一句人話（UI 與 log 共用，避免兩邊各寫一份）。 */
export function peerOutcomeMessage(outcome) {
    if (outcome.status === 'verified') {
        const witness = outcome.checks.find((check) => check.found);
        const who = witness ? `「${witness.nickname}」` : '第二個連線';
        return `${who}看到了這句話 → 已確認送達（${outcome.latencyMs}ms）`;
    }
    if (outcome.status === 'no-peer') {
        return '沒有可用的第二個身分：同房再開一顆帳號（或私訊給池子裡的身分）就能驗';
    }
    if (outcome.status === 'error') {
        return '第二個連線讀不到訊息列表（連線／權限問題），不代表沒送到';
    }
    const names = outcome.checks.map((check) => `「${check.nickname}」`).join('、');
    return `${names}的列表裡還沒有這句話（${outcome.latencyMs}ms）；可能還在站方輪詢佇列，稍後再按一次`;
}
//# sourceMappingURL=delivery-peer.js.map